import { create } from 'zustand';
import { 
  fetchPMUsers as fetchPMUsersApi, 
  fetchPMAccounts as fetchPMAccountsApi,
  fetchCustodians as fetchCustodiansApi,
  validateSecurityCusip,
  createDemoAccountPosition
} from '../http/api';
import { useAccountPositionStore } from './accountPositionStore';

/**
 * Store for managing demo account form state and operations
 * Handles the form data, validation, and API interactions for creating/updating accounts
 */
export const useDemoAccountStore = create((set, get) => ({
  // Reference data
  // pmUsers: [],
  accountIdentifiers: [],
  custodians: [],
  
  // UI state
  isLoading: false,
  error: null,
  success: null,

  // Form data with default values
  formData: {
    // pm: '',
    // pmId: '',
    accountGroup: '',
    accountId: '',
    custodian: '',
    custodianId: '',
    description: '',
    rows: [{ cusip: '', secMasterId: null, bondInventoryId: null, quantity: '', price: '', oldPrice: '' }] // Show a default row on initial render
  },
  
  // Field validation errors
  fieldErrors: {},

  // New state for account identifier check workflow
  accountIdentifierStatus: null, // 'new' | 'map' | 'owned'
  accountIdentifierResponse: null,

  // Reset form to initial state
  resetFormData: () => set({
    formData: {
      // pm: '',
      // pmId: '',
      accountGroup: '',
      accountId: '',
      custodian: '',
      custodianId: '',
      description: '',
      rows: [{ cusip: '', secMasterId: null, bondInventoryId: null, quantity: '', price: '', oldPrice: '' }] // Show a default row when form is reset
    },
    fieldErrors: {},
    error: null,
    success: null,
    accountIdentifierStatus: null,
    accountIdentifierResponse: null
  }),

  // Form data updates
  updateFormData: (data) => set((state) => ({
    formData: { ...state.formData, ...data }
  })),

  updateRowData: (index, data) => set((state) => {
    const newRows = [...state.formData.rows];
    if (index >= 0 && index < newRows.length) {
      newRows[index] = { ...newRows[index], ...data };
      return {
        formData: {
          ...state.formData,
          rows: newRows
        }
      };
    }
    return state;
  }),

  // Row management
  addRow: () => set((state) => ({
    formData: {
      ...state.formData,
      rows: [
        ...state.formData.rows, 
        { cusip: '', secMasterId: null, bondInventoryId: null, quantity: '', price: '', oldPrice: '' }
      ]
    }
  })),

  removeRow: (index) => set((state) => {
    // Allow removing any row, even if it's the last one
    const newRows = state.formData.rows.filter((_, i) => i !== index);
    
    // Don't add an empty row when all rows are removed
    // Let the rows array be empty
    
    return {
      formData: {
        ...state.formData,
        rows: newRows
      }
    };
  }),

  // Error handling
  setFieldErrors: (errors) => set((state) => ({
    fieldErrors: { ...state.fieldErrors, ...errors }
  })),

  clearAllFieldErrors: () => set({ fieldErrors: {} }),

  clearFieldError: (fieldName) => set((state) => ({
    fieldErrors: {
      ...state.fieldErrors,
      [fieldName]: null
    }
  })),

  // Data fetching
  // fetchPMUsers: async (token) => {
  //   if (!token) return [];
    
  //   set({ isLoading: true, error: null });
  //   try {
  //     const data = await fetchPMUsersApi(token);
  //     if (!data || !Array.isArray(data)) {
  //       throw new Error('Invalid response format');
  //     }
  //     set({ pmUsers: data, isLoading: false });
  //     return data;
  //   } catch (error) {
  //     set({ error: `Failed to fetch PM users: ${error.message}`, isLoading: false });
  //     return [];
  //   }
  // },

  // fetchPMAccounts: async (token, pmId) => {
  //   // This function is no longer needed for create mode since we use text input
  //   // Keep it for backward compatibility but return empty array
  //   console.log('fetchPMAccounts called but not needed for text input mode');
  //   return [];
  // },

  fetchCustodians: async (token) => {
    if (!token) return [];
    
    set({ isLoading: true, error: null });
    try {
      const data = await fetchCustodiansApi(token);
      if (!data || !Array.isArray(data)) {
        throw new Error('Invalid response format');
      }
      set({ custodians: data, isLoading: false });
      return data;
    } catch (error) {
      set({ error: `Failed to fetch custodians: ${error.message}`, isLoading: false });
      return [];
    }
  },

  // CUSIP validation
  validateCusip: async (token, cusip, rowIndex) => {
    if (!token || !cusip || cusip.length < 9) {
      // clear any stored IDs and cusipRef for invalid/too-short input
      get().updateRowData(rowIndex, { secMasterId: null, bondInventoryId: null, cusipRef: '' });
      return { valid: false, error: 'CUSIP must be at least 9 characters' };
    }

    try {
      const response = await validateSecurityCusip(token, cusip);
      // response.valid will be false for both non-200 and 200 with success: false
      if (!response || !response.valid) {
        // Clear IDs and cusipRef on invalid response
        get().updateRowData(rowIndex, { secMasterId: null, bondInventoryId: null, cusipRef: '' });
        return { valid: false, error: response?.message || 'Invalid CUSIP' };
      }
      
      // Extract relevant IDs from response
      const secMasterId = response.secMasterDTO?.secMasterId;
      const bondInventoryId = response.id;
      
      if (!secMasterId) {
        get().updateRowData(rowIndex, { secMasterId: null, bondInventoryId: null, cusipRef: '' });
        return { valid: false, error: 'Invalid security master data' };
      }
      
      // Extract price from response and prepare update data
      // Normalize cusip to canonical value from API (if provided) and store cusipRef
      const canonicalCusip = response.cusip || cusip;
      const updateData = { secMasterId, bondInventoryId, cusip: canonicalCusip, cusipRef: response.cusip || '' };
      
      // Add price to update data if available in response and price is empty
      if (
        response.price !== undefined &&
        response.price !== null &&
        (!get().formData.rows[rowIndex].price || get().formData.rows[rowIndex].price === '')
      ) {
        updateData.price = response.price.toString();
      }
      
      // Update the row with the validated data and price
      get().updateRowData(rowIndex, updateData);
      return { valid: true, secMasterId, bondInventoryId, price: response.price };
    } catch (error) {
      // Clear IDs and cusipRef on exception
      get().updateRowData(rowIndex, { secMasterId: null, bondInventoryId: null, cusipRef: '' });
      return { valid: false, error: 'Error validating CUSIP' };
    }
  },

  // Account operations
  createDemoAccount: async (token) => {
    if (!token) {
      throw new Error('No authentication token provided');
    }

    const { accountIdentifierStatus } = get();

    // If mapping an existing account, use update flow so positions are sent in payload
    if (accountIdentifierStatus === 'map') {
      return await get().updateDemoAccount(token);
    }

    // --- existing "new account" creation flow below ---
    const { formData } = get();
    const accountIdentifier = 'ACC-' + formData.accountGroup.trim();

    let positions = formData.rows
      .filter(row => {
        // Skip rows where all three fields are empty
        const isEmpty = 
          (!row.cusip || safeToString(row.cusip).trim() === '') && 
          (row.quantity === '' || row.quantity === null || row.quantity === undefined || safeToString(row.quantity).trim() === '') && 
          (!row.price || safeToString(row.price).trim() === '');
        // Allow quantity 0 as valid (0, '0', or 0.0)
        const hasValidQuantity = 
          row.quantity === 0 || row.quantity === '0' || row.quantity === 0.0 || (!!row.quantity && safeToString(row.quantity).trim() !== '');
        if (isEmpty || !row.cusip || !hasValidQuantity || !row.price || !row.secMasterId) return false;
        if (Number(row.quantity) <= 0) return false;
        return true;
      })
      .map(row => ({
        accountPositionMappingId: 0, // Always 0 for create
        bondInventoryId: row.bondInventoryId,
        secMasterId: row.secMasterId,
        cusip: row.cusip,
        oldPrice: row.price,
        price: Number(row.price),
        quantity: Number(row.quantity)
      }));

    const payload = {
      accountId: 0,
      accountIdentifier: accountIdentifier,
      description: formData.description,
      custodianId: formData.custodianId,
      action: 0, // create new
      positionMapping: positions
    };

    set({ isLoading: true, error: null, success: null });
    try {
      const response = await createDemoAccountPosition(token, payload);
      
       if (!response || response.success === false) {
        const errorMessage = response?.message || 'Failed to create demo account';
        set({ isLoading: false, error: errorMessage });
        throw new Error(errorMessage);
      }
      const accountId = response.summary.accountMstId;
      set({ isLoading: false, success: 'Demo account created successfully' });
      const accountStore = useAccountPositionStore.getState();
      accountStore.showNotification(`Account "${accountIdentifier}" created successfully`);
      const result = {
        id: accountId,
        accountId: accountId,
        description: formData.description,
        custodianName: formData.custodian,
        custodianId: formData.custodianId,
        accountNumber: accountIdentifier,
        accountIdentifier: accountIdentifier,
        accountType: 'Bond',
        userName: formData.userName
      };
      return result;
    } catch (error) {
      set({ isLoading: false, error: error.message || 'Failed to create demo account' });
      throw error;
    }
  },

  updateDemoAccount: async (token, originalPositions = []) => {
    if (!token) {
      throw new Error('No authentication token provided');
    }
    
    const { formData } = get();
    
    // Validate description
    if (!safeToString(formData.description).trim()) {
      throw new Error('Description is required');
    }
    if (safeToString(formData.description).length > 15) {
      throw new Error('Description must be 15 characters or less');
    }

    // Validate custodian
    if (!formData.custodianId) {
      throw new Error('Custodian is required');
    }
    
    // Ensure account identifier has ACC- prefix
    let accountIdentifier = formData.accountGroup;
    if (!accountIdentifier.startsWith('ACC-')) {
      accountIdentifier = 'ACC-' + accountIdentifier;
    }
    
    // Prepare the positions data - filter out completely empty rows
    const positions = formData.rows
      .filter(row => {
        // Skip rows where all three fields are empty or pendingDelete
        const isEmpty = 
          (!row.cusip || safeToString(row.cusip).trim() === '') && 
          (!row.quantity || safeToString(row.quantity).trim() === '') && 
          (!row.price || safeToString(row.price).trim() === '');
        
        if (isEmpty || row.pendingDelete || !row.cusip || !row.quantity || !row.price) return false;
        
        // For new rows, require secMasterId. For existing rows in edit mode, allow it to be missing.
        if (!row.accountPositionMappingId && !row.secMasterId) return false;

        // For edit: quantity must not be 0 (negative or positive allowed)
        if (Number(row.quantity) === 0) return false;
        return true;
      })
      .map(row => {
        // For existing positions, use the original accountPositionMappingId
        // For new positions, use 0
        const accountPositionMappingId = row.accountPositionMappingId || 0;
        
        return {
          accountPositionMappingId,
          bondInventoryId: row.bondInventoryId,
          secMasterId: row.secMasterId,
          cusip: row.cusip,
          price: Number(row.price),
          oldPrice: row.oldPrice ? Number(row.oldPrice) : Number(row.price),
          quantity: Number(row.quantity)
        };
      });

    // Remove validation that requires at least one position

    // Update the request payload to include all editable fields
    const payload = {
      accountId: formData.accountId,
      // pmId: formData.pmId,
      accountIdentifier: accountIdentifier, // Use the properly formatted identifier with ACC- prefix
      description: formData.description.trim(),
      custodianId: formData.custodianId,
      action: 1, // Update action
      positionMapping: positions
    };

    set({ isLoading: true, error: null, success: null });
    
    try {
      const response = await createDemoAccountPosition(token, payload);
      
      if (!response || response.success === false) {
        const errorMessage = response?.message || 'Failed to update demo account';
        set({ isLoading: false, error: errorMessage });
        throw new Error(errorMessage);
      }
      
      set({ isLoading: false, success: 'Demo account updated successfully' });
      // Use the account position store to show notification too
      const { showNotification } = useAccountPositionStore.getState();
      showNotification(`Demo account "${accountIdentifier}" updated successfully`);
      
      const result = {
        account: {
          id: formData.accountId,
          accountId: formData.accountId,
          description: formData.description,
          custodianName: formData.custodian,
          custodianId: formData.custodianId,
          accountNumber: formData.accountGroup,
          accountIdentifier: formData.accountGroup,
          accountType: 'Bond',
          // pmId: formData.pmId,
          userName: formData.userName
        },
        positions: positions
      };
      
      return result;
    } catch (error) {
      set({ isLoading: false, error: error.message || 'Failed to update demo account' });
      throw error;
    }
  },

  // Utility methods
  setAccountIdentifiers: (identifiers) => set({
    accountIdentifiers: identifiers
  }),

  // Helper setters for account identifier check
  setAccountIdentifierStatus: (status, response = null) => set({
    accountIdentifierStatus: status,
    accountIdentifierResponse: response
  }),
  clearAccountIdentifierStatus: () => set({
    accountIdentifierStatus: null,
    accountIdentifierResponse: null
  }),

  clearError: () => set({ error: null }),

  clearSuccess: () => set({ success: null }),
  
  // Populate form data from existing account (for editing)
  populateFromAccount: (account) => {
    if (!account) return;
    
    // Format positions data for editing - don't add default row if no positions exist
    const rows = account.positions && account.positions.length > 0 
      ? account.positions.map(pos => ({
          cusip: pos.cusip,
          cusipRef: pos.cusipRef || '', // include cusipRef when available
          secMasterId: pos.secMasterId,
          bondInventoryId: pos.bondInventoryId,
          quantity: pos.quantity?.toString() || '',
          price: pos.price?.toString() || '',
          oldPrice: pos.price?.toString() || '', // Store original price for comparison
          accountPositionMappingId: pos.accountPositionMappingId,
          pendingDelete: false // Always set to false when loading from server
        }))
      : []; // Empty array when editing an account without positions

    // Extract account identifier without ACC- prefix for the form
    let accountGroupValue = account.accountIdentifier || '';
    if (accountGroupValue.startsWith('ACC-')) {
      accountGroupValue = accountGroupValue.substring(4);
    }

    // Update form state with account data
    set({
      formData: {
        userName: account.userName || '',
        // pmId: account.pmId || '',
        accountGroup: accountGroupValue, // Store without ACC- prefix
        accountId: account.accountId || '',
        custodian: account.custodianName || '',
        custodianId: account.custodianId || '',
        description: account.description || '',
        rows: rows
      },
      accountIdentifiers: []
    });
  }
}));

// Helper function to safely convert value to string
const safeToString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};
